import { IconSwitchHorizontal } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { BenefitDocumentDto, EmployerDocumentDto } from '@zorro/clients';
import {
  DocumentCard,
  DocumentCardMenuItem,
} from '@zorro/design-system/v2/DocumentCard';
import {
  downloadBenefitDocument,
  useMonolithMutation,
} from '@zorro/web/ui-utils';

import { buildDocumentCardDate } from '../DocumentListSection/documentCardUtils';

type Props = {
  document: BenefitDocumentDto | EmployerDocumentDto;
  /**
   * Whether the viewer may rename and re-share this document. Both PATCH the
   * row through a back-office-only endpoint, so this stays off for employees.
   * Required on purpose: defaulting it once let a caller forget the prop and
   * hand employees the full manage menu.
   */
  canEdit: boolean;
  /**
   * Passing it is what shows the delete action, independently of `canEdit`:
   * an employee may remove a document from their own Files tab without also
   * gaining rename or re-share. Callers that must not offer delete — the
   * read-only back-office persona — leave it out.
   */
  onDelete?: (document: BenefitDocumentDto | EmployerDocumentDto) => void;
  onDownload?: () => Promise<void>;
  /** Names of every other document in the Files tab, to reject a duplicate rename. */
  existingFileNames?: string[];
};

const isBenefitDocument = (
  doc: BenefitDocumentDto | EmployerDocumentDto
): doc is BenefitDocumentDto => 'isSharedWithEmployee' in doc;

export const BenefitDocumentCard = ({
  document,
  canEdit,
  onDelete,
  onDownload,
  existingFileNames,
}: Props) => {
  const queryClient = useQueryClient();
  const { mutate: updateDocument } = useMonolithMutation({
    method: 'benefitDocumentsControllerUpdateBenefitDocument',
    successMessage: 'Document updated',
    // A one-field PATCH must not refetch the whole page; only the lists change.
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
  });

  // Same PATCH without the toast: the changed name is its own confirmation.
  const { mutate: renameDocument } = useMonolithMutation({
    method: 'benefitDocumentsControllerUpdateBenefitDocument',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
  });

  const refetchDocumentLists = () =>
    queryClient.invalidateQueries({
      queryKey: [
        'benefitDocumentsControllerGetBenefitDocumentsByOnboardingPeriod',
      ],
    });

  const benefitDocument = isBenefitDocument(document) ? document : undefined;
  const description = isBenefitDocument(document)
    ? undefined
    : (document.description ?? undefined);

  const handleToggleVisibility = async () => {
    if (!benefitDocument) {
      return;
    }
    await updateDocument([
      benefitDocument.id,
      { isSharedWithEmployee: !benefitDocument.isSharedWithEmployee },
    ]);
    await refetchDocumentLists();
  };

  const handleDownload = async () => {
    if (onDownload) {
      await onDownload();
      return;
    }
    await downloadBenefitDocument(document.documentUrl, document.fileName);
  };

  const handleRename = async (fileName: string) => {
    if (!benefitDocument) {
      return;
    }
    await renameDocument([benefitDocument.id, { fileName }]);
    await refetchDocumentLists();
  };

  /**
   * The ICHRA letter is generated on the fly, with no persisted row behind it,
   * so it is the one card with nothing to rename, reshare, or delete.
   */
  const isPersisted = Boolean(benefitDocument) && !onDownload;

  const isManageable = isPersisted && canEdit;

  const menuItems: DocumentCardMenuItem[] | undefined =
    benefitDocument && isManageable
      ? [
          {
            label: `Change to ${benefitDocument.isSharedWithEmployee ? 'admin' : 'employee'} document`,
            icon: <IconSwitchHorizontal size={16} stroke={2} />,
            onClick: handleToggleVisibility,
          },
        ]
      : undefined;

  return (
    <DocumentCard
      variant="border"
      fileName={document.fileName}
      description={description}
      date={buildDocumentCardDate(document.createdAt)}
      onDownload={handleDownload}
      menuItems={menuItems}
      onRename={isManageable ? handleRename : undefined}
      existingFileNames={existingFileNames}
      onDelete={isPersisted && onDelete ? () => onDelete(document) : undefined}
    />
  );
};
